import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { changePassword } from "@/api/profile-api";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

type ChangePasswordFormValues = {
  newPassword: string;
  confirmPassword: string;
};

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps) {
  const { t } = useTranslation('layout');
  const { toast } = useToast();

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(
      z
        .object({
          newPassword: z
            .string()
            .min(8, t('changePassword.validationMinLength'))
            .max(100, t('changePassword.validationMaxLength')),
          confirmPassword: z
            .string()
            .min(8, t('changePassword.validationMinLength'))
            .max(100, t('changePassword.validationMaxLength')),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t('changePassword.validationMismatch'),
          path: ["confirmPassword"],
        })
    ),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [open, form]);

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    try {
      const response = await changePassword({
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      if (response.success) {
        toast({
          title: t('changePassword.success'),
          description: response.message || t('changePassword.successMessage'),
        });
        onOpenChange(false);
      } else {
        toast({
          title: t('changePassword.failed'),
          description: response.message || t('changePassword.failedMessage'),
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: t('changePassword.failed'),
        description: error?.message || t('changePassword.errorMessage'),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('changePassword.title')}</DialogTitle>
          <DialogDescription>
            {t('changePassword.description')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('changePassword.newPassword')}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t('changePassword.newPasswordPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('changePassword.confirmPassword')}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t('changePassword.confirmPasswordPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={form.formState.isSubmitting}
              >
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('changePassword.changing')}
                  </div>
                ) : (
                  t('changePassword.changeButton')
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
